/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { usePress } from '../hooks/UNSAFE_usePress';
import { DropdownArrowIcon } from '../PRIVATE_ThemedIcons/DropdownArrowIcon';
import { classNames } from '../utils/UNSAFE_classNames';

type Props = {
  isDisabled?: boolean;
  onClick?: () => void;
  size?: 'md' | 'sm';
};

// TODO: replace this with var(--oj-button-height) once it is available
const ojButtonHeight = '2.75rem';
// TODO: replace this with var(--oj-button-sm-height) once it is available
const ojButtonSmHeight = '2.25rem';
// TODO: replace this with var(--oj-button-border-radius) once it is available
const ojButtonBorderRadius = 'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-md)';
// TODO: replace this with var(--oj-button-borderless-chrome-text-color) once it is available
const ojButtonBorderlessChromeTextColor = 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)';
// TODO: replace this with var (--oj-button-borderless-chrome-text-color-disabled) once it is available
const ojButtonBorderlessChromeTextColorDisabled =
  'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)';
// TODO: replace this with var(--oj-button-borderless-chrome-text-color-hover) once it is available
const ojButtonBorderlessChromeTextColorHover =
  'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)';
// TODO: replace this with var(--oj-button-borderless-chrome-bg-color-hover) once it is available
const ojButtonBorderlessChromeBgColorHover = 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-hover)';
// TODO: replace this with var(--oj-button-borderless-chrome-border-color-hover) once it is available
const ojButtonBorderlessChromeBorderColorHover = 'transparent';
// TODO: replace this with var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-size) once it is available
const ojButtonIconSize = 'var(--oj-c-PRIVATE-DO-NOT-USE-core-icon-size-lg)';

const rootStyles = {
  base: cssProps`${{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',

    paddingInline: 0,
    paddingBlock: 0,
    borderRadius: ojButtonBorderRadius,
    color: ojButtonBorderlessChromeTextColor,
    fontSize: ojButtonIconSize
  }}`,

  sizeMd: cssProps`${{
    width: ojButtonHeight,
    height: ojButtonHeight,
    lineHeight: ojButtonHeight
  }}`,

  sizeSm: cssProps`${{
    width: ojButtonSmHeight,
    height: ojButtonSmHeight,
    lineHeight: ojButtonSmHeight
  }}`,

  enabled: cssProps`${{
    cursor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable)',

    '&:hover': {
      color: ojButtonBorderlessChromeTextColorHover,
      backgroundColor: ojButtonBorderlessChromeBgColorHover,
      borderColor: ojButtonBorderlessChromeBorderColorHover
    }
  }}`,

  disabled: cssProps`${{
    color: ojButtonBorderlessChromeTextColorDisabled
  }}`
};

const noop = () => {};

export function DropdownArrow({ isDisabled = false, onClick, size = 'md' }: Props) {
  const preventDefault = useCallback((event: Event) => {
    event.preventDefault();
  }, []);
  const { pressProps } = usePress(onClick ?? noop, { isDisabled });

  const styleClasses = classNames([
    rootStyles.base,
    size === 'sm' ? rootStyles.sizeSm : rootStyles.sizeMd,
    isDisabled ? rootStyles.disabled : rootStyles.enabled
  ]);

  return (
    <span
      aria-hidden="true"
      class={styleClasses}
      tabIndex={-1}
      onMouseDown={preventDefault}
      {...pressProps}>
      <DropdownArrowIcon />
    </span>
  );
}
